// This file is auto-generated by @hey-api/openapi-ts

import type { Client, Options as Options2, TDataShape } from './client';
import { client } from './client.gen';
import type { AddTagsOnSubscriptionData, AddTagsOnSubscriptionErrors, AddTagsOnSubscriptionResponses, CreateDirectSubscriptionData, CreateDirectSubscriptionErrors, CreateDirectSubscriptionResponses, GetActiveSubscriptionData, GetActiveSubscriptionErrors, GetActiveSubscriptionResponses, GetLatestSubscriptionData, GetLatestSubscriptionErrors, GetLatestSubscriptionResponses, GetPipelineFailuresData, GetPipelineFailuresErrors, GetPipelineFailuresResponses, GetPipelineTagsData, GetPipelineTagsErrors, GetPipelineTagsResponses, GetSiteElectricMetersData, GetSiteElectricMetersErrors, GetSiteElectricMetersResponses, InvalidateEnedisPdlData, InvalidateEnedisPdlErrors, InvalidateEnedisPdlResponses, MoveSiteToHouseHolderData, MoveSiteToHouseHolderErrors, MoveSiteToHouseHolderResponses, OverrideEnedisPdlValidationData, OverrideEnedisPdlValidationErrors, OverrideEnedisPdlValidationResponses, RegisterSitePdlManuallyData, RegisterSitePdlManuallyErrors, RegisterSitePdlManuallyResponses, RemoveTagsOnSubscriptionData, RemoveTagsOnSubscriptionErrors, RemoveTagsOnSubscriptionResponses, ReOpenSubscriptionData, ReOpenSubscriptionErrors, ReOpenSubscriptionResponses, ReturnSiteToLegacyData, ReturnSiteToLegacyErrors, ReturnSiteToLegacyResponses, SearchEnedisPointsData, SearchEnedisPointsErrors, SearchEnedisPointsResponses, SearchSiteSubscriptionsHistoryData, SearchSiteSubscriptionsHistoryErrors, SearchSiteSubscriptionsHistoryResponses, ValidateSitePdlFromSearchData, ValidateSitePdlFromSearchErrors, ValidateSitePdlFromSearchResponses } from './types.gen';

export type Options<TData extends TDataShape = TDataShape, ThrowOnError extends boolean = boolean> = Options2<TData, ThrowOnError> & {
    /**
     * You can provide a client instance returned by `createClient()` instead of
     * individual options. This might be also useful if you want to implement a
     * custom client.
     */
    client?: Client;
    /**
     * You can pass arbitrary values through the `meta` object. This can be
     * used to access values that aren't defined as part of the SDK function.
     */
    meta?: Record<string, unknown>;
};

/**
 * Force the validation of an Enedis delivery point
 *
 * Marks the delivery point as OVERRIDDEN: the operator vouches for it without any Enedis confirmation. The delivery point must already be known — it is recorded either by /site/{siteId}/register-manually or by a search validation. Nothing else changes: the Enedis and Voltalis data already recorded are kept as is. Forcing a delivery point that is already OVERRIDDEN is accepted and only refreshes the operator and the date-time. Fails on a delivery point already VALIDATED — domain error code ALREADY_VALID, there is nothing left to force — and on an unknown delivery point (technical error, invalid reference).
 *
 */
export const overrideEnedisPdlValidation = <ThrowOnError extends boolean = false>(options: Options<OverrideEnedisPdlValidationData, ThrowOnError>) => (options.client ?? client).put<OverrideEnedisPdlValidationResponses, OverrideEnedisPdlValidationErrors, ThrowOnError>({ url: '/enedis-pdl-management/{deliveryPoint}/override-validation', ...options });

/**
 * Invalidate an Enedis delivery point
 *
 * Marks the delivery point as INVALIDATED: the operator walks back a validation that turned out to be wrong, so the site can be searched or registered again. Nothing else changes: the Enedis and Voltalis data already recorded are kept as is. Invalidating a delivery point that is already INVALIDATED is accepted and only refreshes the operator and the date-time. Fails on a delivery point that has never been validated — domain error code NOT_ELIGIBLE_TO_INVALIDATION, a merely registered delivery point has no validation to walk back — and on an unknown delivery point (technical error, invalid reference).
 *
 */
export const invalidateEnedisPdl = <ThrowOnError extends boolean = false>(options: Options<InvalidateEnedisPdlData, ThrowOnError>) => (options.client ?? client).put<InvalidateEnedisPdlResponses, InvalidateEnedisPdlErrors, ThrowOnError>({ url: '/enedis-pdl-management/{deliveryPoint}/invalidate', ...options });

/**
 * Run an Enedis point search
 *
 * Blocking call: the search is played by the Enedis Orchestrator and the response only comes back once no further query level will be played. When a level returns no point, the search is automatically replayed with degraded criteria, level by level, until points are found or the chain is exhausted. The municipality of the search (postal and insee codes) is read from the site, which must be in France.
 *
 */
export const searchEnedisPoints = <ThrowOnError extends boolean = false>(options: Options<SearchEnedisPointsData, ThrowOnError>) => (options.client ?? client).post<SearchEnedisPointsResponses, SearchEnedisPointsErrors, ThrowOnError>({
    url: '/enedis-pdl-management/search',
    ...options,
    headers: {
        'Content-Type': 'application/json',
        ...options.headers
    }
});

/**
 * Return a site to legacy
 *
 * Returns a site to the legacy system. Fails if a subscription is currently running on the site.
 */
export const returnSiteToLegacy = <ThrowOnError extends boolean = false>(options: Options<ReturnSiteToLegacyData, ThrowOnError>) => (options.client ?? client).put<ReturnSiteToLegacyResponses, ReturnSiteToLegacyErrors, ThrowOnError>({ url: '/site/{siteId}/return-to-legacy', ...options });

/**
 * Move a site to the householder segment
 *
 * Moves a site to the householder business segment. Fails if the site is already a householder or an organization site, has a planned appointment, has no subscriber or no layout, or has a professional layout while its subscriber is not a small company. On success, any active legacy phoning mission of the site is closed (stop reason "Other") and replaced by a "No mission" phoning mission starting at the closing date-time.
 *
 */
export const moveSiteToHouseHolder = <ThrowOnError extends boolean = false>(options: Options<MoveSiteToHouseHolderData, ThrowOnError>) => (options.client ?? client).put<MoveSiteToHouseHolderResponses, MoveSiteToHouseHolderErrors, ThrowOnError>({ url: '/site/{siteId}/move-to-householder', ...options });

/**
 * Read the electric meters of a site
 *
 * Returns the electric meters known for the site, along with the business segment of the site. A meter operated by Enedis carries its delivery point validation status; a meter operated by another DSO, or by no DSO yet, carries none. Fails if the site is not an organization and several meters are attached to it.
 *
 */
export const getSiteElectricMeters = <ThrowOnError extends boolean = false>(options: Options<GetSiteElectricMetersData, ThrowOnError>) => (options.client ?? client).get<GetSiteElectricMetersResponses, GetSiteElectricMetersErrors, ThrowOnError>({ url: '/site/{siteId}/electric-meter', ...options });

/**
 * Validate the site delivery point from an Enedis search result
 *
 * Validates the electric meter of the site from one result of an Enedis delivery point search, as picked by the operator. The Enedis data carried by the payload is crossed with the Voltalis data known for the site. Fails if the site is an organization, has no subscriber or no address.
 *
 */
export const validateSitePdlFromSearch = <ThrowOnError extends boolean = false>(options: Options<ValidateSitePdlFromSearchData, ThrowOnError>) => (options.client ?? client).put<ValidateSitePdlFromSearchResponses, ValidateSitePdlFromSearchErrors, ThrowOnError>({
    url: '/site/{siteId}/validate-from-search',
    ...options,
    headers: {
        'Content-Type': 'application/json',
        ...options.headers
    }
});

/**
 * Register the site delivery point from a manual operator input
 *
 * Registers the delivery point typed by the operator on the electric meter of the site. A French site whose delivery point matches the Enedis format is registered as an Enedis meter, along with a not-yet-validated Enedis delivery point information; any other delivery point is registered on a generic meter, with no Enedis information at all. Fails if the site is an organization, or if the meter is already operated by Enedis while the delivery point does not match the Enedis format.
 *
 */
export const registerSitePdlManually = <ThrowOnError extends boolean = false>(options: Options<RegisterSitePdlManuallyData, ThrowOnError>) => (options.client ?? client).put<RegisterSitePdlManuallyResponses, RegisterSitePdlManuallyErrors, ThrowOnError>({
    url: '/site/{siteId}/register-manually',
    ...options,
    headers: {
        'Content-Type': 'application/json',
        ...options.headers
    }
});

/**
 * Get failure categories and reasons for a pipeline
 *
 * Retrieves the failure categories and their underlying reasons for a given subscription pipeline
 */
export const getPipelineFailures = <ThrowOnError extends boolean = false>(options: Options<GetPipelineFailuresData, ThrowOnError>) => (options.client ?? client).get<GetPipelineFailuresResponses, GetPipelineFailuresErrors, ThrowOnError>({ url: '/pipeline/{pipelineId}/failures', ...options });

/**
 * List available tags for a pipeline
 *
 * Retrieves the tree of tags available for subscriptions in a given pipeline
 */
export const getPipelineTags = <ThrowOnError extends boolean = false>(options: Options<GetPipelineTagsData, ThrowOnError>) => (options.client ?? client).get<GetPipelineTagsResponses, GetPipelineTagsErrors, ThrowOnError>({ url: '/pipeline/{pipelineId}/tags', ...options });

/**
 * Create a direct acquisition subscription on an existing site and subscriber
 *
 * Creates a DIRECT_ACQUISITION subscription; pipeline (default for the site country) and partner (pipeline's direct acquisition partner) are resolved server-side
 */
export const createDirectSubscription = <ThrowOnError extends boolean = false>(options: Options<CreateDirectSubscriptionData, ThrowOnError>) => (options.client ?? client).post<CreateDirectSubscriptionResponses, CreateDirectSubscriptionErrors, ThrowOnError>({
    url: '/subscription',
    ...options,
    headers: {
        'Content-Type': 'application/json',
        ...options.headers
    }
});

/**
 * Get active subscription for a site
 *
 * Retrieves the active subscription for a given site
 */
export const getActiveSubscription = <ThrowOnError extends boolean = false>(options: Options<GetActiveSubscriptionData, ThrowOnError>) => (options.client ?? client).get<GetActiveSubscriptionResponses, GetActiveSubscriptionErrors, ThrowOnError>({ url: '/subscription/active/{siteId}', ...options });

/**
 * Get latest subscription for a site
 *
 * Retrieves the latest subscription for a given site
 */
export const getLatestSubscription = <ThrowOnError extends boolean = false>(options: Options<GetLatestSubscriptionData, ThrowOnError>) => (options.client ?? client).get<GetLatestSubscriptionResponses, GetLatestSubscriptionErrors, ThrowOnError>({ url: '/subscription/latest/{siteId}', ...options });

/**
 * List history of subscriptions for a site
 *
 * Searches and paginates through subscriptions using data table format
 */
export const searchSiteSubscriptionsHistory = <ThrowOnError extends boolean = false>(options: Options<SearchSiteSubscriptionsHistoryData, ThrowOnError>) => (options.client ?? client).post<SearchSiteSubscriptionsHistoryResponses, SearchSiteSubscriptionsHistoryErrors, ThrowOnError>({
    url: '/subscription/history/{siteId}',
    ...options,
    headers: {
        'Content-Type': 'application/json',
        ...options.headers
    }
});

/**
 * Re-open a failed subscription
 *
 * Re-opens a previously failed subscription
 */
export const reOpenSubscription = <ThrowOnError extends boolean = false>(options: Options<ReOpenSubscriptionData, ThrowOnError>) => (options.client ?? client).put<ReOpenSubscriptionResponses, ReOpenSubscriptionErrors, ThrowOnError>({ url: '/subscription/{subscriptionId}/re-open', ...options });

/**
 * Add tags to a subscription
 *
 * Adds one or more pipeline tags to a subscription by path
 */
export const addTagsOnSubscription = <ThrowOnError extends boolean = false>(options: Options<AddTagsOnSubscriptionData, ThrowOnError>) => (options.client ?? client).put<AddTagsOnSubscriptionResponses, AddTagsOnSubscriptionErrors, ThrowOnError>({
    url: '/subscription/{subscriptionId}/add-tag',
    ...options,
    headers: {
        'Content-Type': 'application/json',
        ...options.headers
    }
});

/**
 * Remove tags from a subscription
 *
 * Removes one or more tags from a subscription by path
 */
export const removeTagsOnSubscription = <ThrowOnError extends boolean = false>(options: Options<RemoveTagsOnSubscriptionData, ThrowOnError>) => (options.client ?? client).put<RemoveTagsOnSubscriptionResponses, RemoveTagsOnSubscriptionErrors, ThrowOnError>({
    url: '/subscription/{subscriptionId}/remove-tag',
    ...options,
    headers: {
        'Content-Type': 'application/json',
        ...options.headers
    }
});
